import {
    useLoaderData,
    useLocation,
    useNavigation,
    useSearchParams,
} from 'react-router';

import type { loader as ourProductsLoader } from '@modules/professional/page/loaders/our-products-page-loader.server';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { useProfileFamily } from '@modules/professional/hook/use-profile-family';
import {
    FAMILY_BRANDS_PARAM,
    resolveFamilyBrandScope,
} from '@modules/professional/util/family-brand-filter';

export interface FamilyBrandOption {
    segment: string;
    title: string;
    /** Brand mark, so the quick-filter bar can toggle on the logo alone. */
    logo: string;
    logoBackground: string;
    checked: boolean;
    /** Toggling navigates here, so the selection stays shareable. */
    to: string;
}

/**
 * The Products tab's brand-family selection, as toggle links.
 *
 * This profile first, then the rest of the family. Ticked state comes from the
 * scope the loader actually applied (`brandScope`) rather than from reading the
 * URL, so the default — own products only, plus anything explicitly shared with
 * this profile — is reflected honestly.
 *
 * While a toggle is in flight the ticks come from the pending URL instead, so a
 * click registers immediately rather than after the search round trip. That runs
 * the server's own {@link resolveFamilyBrandScope} over the pending URL, using
 * the fallback the loader reports, so the optimistic answer is the same answer
 * the loader will return — including when the URL names no brands at all, where
 * the scope is this profile plus whatever has shared products with it.
 *
 * Clearing the last brand has to resolve to that fallback rather than linger on
 * the old scope: the toggle links are built from whatever these ticks show, so a
 * stale scope here would rebuild the param from brands the visitor just cleared
 * and quietly undo the clear.
 *
 * Shared by the quick-filter bar and the filter drawer's brand group so the two
 * can't disagree about what is on.
 */
export const useFamilyBrandFilter = (): FamilyBrandOption[] => {
    const { brandScope = [], brandFallback = [] } =
        useLoaderData<typeof ourProductsLoader>();
    const { Title, URLSegment, CompanyLogo, LogoBackgroundColor } =
        useProfessionalInitialRouteData();
    const family = useProfileFamily();
    const [searchParams] = useSearchParams();
    const { pathname } = useLocation();
    const navigation = useNavigation();

    if (!family) return [];

    // Same-tab navigations only: a pending move to another tab or profile says
    // nothing about this tab's scope.
    const pendingSearch =
        navigation.state === 'loading' &&
        navigation.location?.pathname === pathname
            ? new URLSearchParams(navigation.location.search)
            : null;

    // `brandFallback` already carries this profile and its product-sharing
    // partners, so the selectable set is that plus the rest of the family —
    // the same list the loader allows.
    const fallback = brandFallback.length > 0 ? brandFallback : [URLSegment];
    const selectable = [
        ...new Set([
            ...fallback,
            ...family.others.map((brand) => brand.segment),
        ]),
    ];
    const appliedScope = pendingSearch
        ? resolveFamilyBrandScope(pendingSearch, { selectable, fallback })
        : brandScope;

    return [
        {
            segment: URLSegment,
            title: Title,
            logo: CompanyLogo,
            logoBackground: LogoBackgroundColor,
        },
        ...family.others.map((brand) => ({
            segment: brand.segment,
            title: brand.title,
            logo: brand.logo,
            logoBackground: brand.logoBackground,
        })),
    ].map((brand) => {
        const checked = appliedScope.includes(brand.segment);
        // Chained off the same scope the ticks show, so clicking two brands in
        // quick succession adds both instead of the second overwriting the
        // first with a link built from the pre-click state.
        const next = checked
            ? appliedScope.filter((segment) => segment !== brand.segment)
            : [...appliedScope, brand.segment];
        const params = new URLSearchParams(searchParams);
        // An empty selection drops the param, which the loader reads as "just
        // this profile" — so unticking everything can't empty the grid.
        if (next.length > 0) {
            params.set(FAMILY_BRANDS_PARAM, next.join(','));
        } else {
            params.delete(FAMILY_BRANDS_PARAM);
        }
        const query = params.toString();
        return {
            ...brand,
            checked,
            to: query ? `${pathname}?${query}` : pathname,
        };
    });
};
